import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, MapPin, Users, Anchor, Sailboat, Home, Star } from 'lucide-react';

const OWNER_PHOTO = 'https://customer-assets-jai6qajn.emergentagent.net/job_289c2f01-5acc-4970-a74f-e64e3b0ff079/artifacts/dxmmp6aa_IMG_1419%20%281%29.JPG';

const highlights = [
  { icon: <MapPin className="w-5 h-5" />, label: 'Born & raised in Santorini' },
  { icon: <Users className="w-5 h-5" />, label: '20+ years in tourism' },
  { icon: <Anchor className="w-5 h-5" />, label: 'Marine tourism experience' },
  { icon: <Home className="w-5 h-5" />, label: 'One of Santorini\'s oldest families' },
];

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-[#FAFAF7] pb-16 md:pb-0" data-testid="about-page">
      <Helmet>
        <title>About Georgios Arvanitis — Santorini Best Experience</title>
        <meta name="description" content="Meet Georgios Arvanitis — born and raised in Santorini, with 20+ years of experience in transfers, tours and authentic Santorinian hospitality." />
      </Helmet>

      {/* Cyclades hero band — Aegean blue with whitewash overlay */}
      <div className="relative bg-gradient-to-br from-[#004E82] via-[#005A9C] to-[#0071C2] overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '24px 24px'
        }} />
        <div className="relative max-w-6xl mx-auto px-6 pt-8 pb-32">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/90 hover:text-white text-sm font-medium mb-8 transition-colors"
            data-testid="about-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
          <div className="max-w-3xl">
            <span className="inline-block px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm border border-white/25 text-white text-xs uppercase tracking-widest font-semibold mb-5">
              About the owner
            </span>
            <h1 className="text-5xl md:text-6xl font-bold text-white leading-[1.05] tracking-tight">
              Georgios Arvanitis
            </h1>
            <p className="mt-4 text-xl text-blue-100 font-light">
              Your local host in Santorini · Family tradition since generations
            </p>
          </div>
        </div>
        {/* Whitewashed curve at bottom — Cyclades architecture reference */}
        <svg className="absolute bottom-0 left-0 right-0 w-full" viewBox="0 0 1440 60" preserveAspectRatio="none" style={{height: '48px'}}>
          <path d="M0,60 C240,20 480,20 720,40 C960,60 1200,60 1440,40 L1440,60 Z" fill="#FAFAF7" />
        </svg>
      </div>

      {/* Photo + Intro */}
      <section className="max-w-6xl mx-auto px-6 relative z-10">
        <div className="grid md:grid-cols-[320px_1fr] gap-10 md:gap-16 items-start">
          {/* Photo card — overlaps into light section */}
          <div className="relative md:-mt-24" data-testid="owner-photo-block">
            <div className="absolute -inset-3 bg-gradient-to-br from-[#005A9C]/20 to-amber-400/20 rounded-3xl blur-2xl" />
            <div className="relative bg-white rounded-3xl overflow-hidden shadow-2xl shadow-[#005A9C]/15 border border-white ring-1 ring-slate-200/60">
              <img
                src={OWNER_PHOTO}
                alt="Georgios Arvanitis"
                className="w-full aspect-[4/5] object-cover"
              />
              <div className="p-5 bg-white border-t border-slate-100">
                <p className="text-xs uppercase tracking-widest text-[#005A9C] font-bold">Owner & Host</p>
                <p className="text-lg font-bold text-slate-900 mt-1">Georgios Arvanitis</p>
                <p className="text-sm text-slate-500">Santorini, Greece</p>
              </div>
            </div>
          </div>

          {/* Highlights — no negative margin, sits cleanly in the light band */}
          <div className="pt-4 md:pt-6">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 leading-tight">
              A lifetime of Santorini,<br />
              <span className="text-[#005A9C]">shared with every guest.</span>
            </h2>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              I was born and raised in Santorini — an island I know not just as my place of origin,
              but as a way of life. For over <strong className="text-slate-900">20 years</strong> I have
              been active in the tourism industry, welcoming guests from around the world.
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              {highlights.map((h) => (
                <div
                  key={h.label}
                  className="flex items-center gap-3 p-3 rounded-xl bg-white border border-slate-200 shadow-sm"
                >
                  <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-[#005A9C]/10 text-[#005A9C]">
                    {h.icon}
                  </span>
                  <span className="text-sm font-medium text-slate-700">{h.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Story sections — Greek postcard style with alternating layouts */}
      <section className="max-w-4xl mx-auto px-6 py-20 space-y-14">
        <StoryBlock
          number="01"
          title="Family roots that shaped an island"
          body="I come from one of the oldest and largest families in Santorini, with strong ties to the island and a long-standing presence in local life. For generations, my family has been active in tourism, hospitality, dining, boating, and fishing. Through this family tradition, I learned to love the island, respect its people, and share its true character with every visitor."
        />
        <StoryBlock
          number="02"
          title="From roads to the sea — full-service local hospitality"
          body="My professional background includes transportation services with cars and vans, as well as activities in the boating and nautical tourism sector. For me, every visitor is unique, and my goal is to provide them with safety, reliability, and authentic Santorinian hospitality."
          icon={<Sailboat className="w-5 h-5" />}
        />
        <StoryBlock
          number="03"
          title="More than a service — a personal experience"
          body="My years of experience and personal knowledge of Santorini allow me to offer something more than just a service: a reliable, personal, and authentic hospitality experience, tailored around the needs of each guest."
          icon={<Star className="w-5 h-5" />}
        />
      </section>

      {/* CTA band — Aegean blue with amber sunset button */}
      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#004E82] to-[#005A9C] p-10 md:p-14 text-center">
          <div className="absolute inset-0 opacity-[0.08]" style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
            backgroundSize: '20px 20px'
          }} />
          <div className="relative">
            <h3 className="text-3xl md:text-4xl font-bold text-white leading-tight">
              Ready to experience Santorini<br className="hidden md:block" /> the local way?
            </h3>
            <p className="mt-4 text-blue-100 max-w-xl mx-auto">
              Book your transfer or private tour with a Santorini local who knows every corner of the island.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/book"
                className="inline-flex items-center gap-2 px-14 h-14 rounded-full bg-white text-[#0A2540] font-medium text-sm tracking-[0.2em] uppercase shadow-xl hover:bg-slate-100 transition-all hover:-translate-y-0.5"
                data-testid="about-book-cta"
              >
                Book Your Ride
              </Link>
              <a
                href="https://wa.me/306974994577?text=Hi%20Georgios!%20I%20read%20about%20you%20and%20I'd%20like%20to%20book%20a%20transfer."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-10 h-14 rounded-full bg-white/10 border border-white/40 text-white font-medium text-sm tracking-[0.2em] uppercase hover:bg-white/20 backdrop-blur-sm transition-all"
                data-testid="about-whatsapp-cta"
              >
                Message on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function StoryBlock({ number, title, body, icon }) {
  return (
    <div className="grid md:grid-cols-[120px_1fr] gap-6 items-start">
      <div className="flex items-center gap-3 md:flex-col md:items-start md:gap-2">
        <span className="text-5xl md:text-6xl font-bold text-[#005A9C]/15 tabular-nums leading-none">
          {number}
        </span>
        {icon && (
          <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-[#005A9C]/10 text-[#005A9C] md:mt-1">
            {icon}
          </span>
        )}
      </div>
      <div className="border-l-2 border-[#005A9C]/20 pl-6 py-1">
        <h3 className="text-xl md:text-2xl font-bold text-slate-900 leading-tight">{title}</h3>
        <p className="mt-3 text-slate-600 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}
